'use client';

import { Icon } from '@iconify-icon/react';
import Image from 'next/image';
import Link from 'next/link';

/**
 * Asymmetric customer-stories layout: one featured story + two stacked.
 * Wordmarks are typographic placeholders until logo files are provided.
 */

const featured = {
  quote:
    'Case-specific research that used to take our analysts hours now takes minutes — and every answer comes with the record citation to back it up.',
  descriptor: 'Energy consulting & regulatory strategy',
  detail:
    'MAXeta runs dedicated Energy Expert Agents over tens of thousands of pages of regulatory filings — every answer traceable to the source document.',
  image: '/images/landing/maxeta-energy.png',
  caseStudy: '/case-studies/maxeta-energy',
  wordmark: (
    <Image
      src="/images/landing/logos/maxeta.png"
      alt="MAXeta Energy, PLLC"
      width={703}
      height={200}
      className="h-8 w-auto"
    />
  ),
};

const stories = [
  {
    quote:
      'We used to print thousands of pages of teacher manuals every year. Now our faculty gets instant, cited answers through our own Faculty Portal.',
    company: 'Calvario City Christian Academy & Pre-School',
    descriptor: 'Private K-12 education',
    image: '/images/landing/calvario-academy.png',
    imagePos: 'object-[50%_18%]',
    caseStudy: '/case-studies/calvario-academy',
    wordmark: (
      <Image
        src="/images/landing/logos/calvario.png"
        alt="Calvario City Christian Academy & Pre-School"
        width={633}
        height={200}
        className="h-9 w-auto"
      />
    ),
  },
  {
    quote:
      'Our team went from spending hours digging through client files to getting actionable, sourced insights in minutes.',
    company: 'Sosa & Arvelo',
    descriptor: 'Strategy, risk & financial advisory',
    image: '/images/landing/sosa-arvelo.png',
    imagePos: 'object-[50%_70%]',
    caseStudy: null,
    wordmark: (
      <Image
        src="/images/landing/logos/sosa-arvelo.png"
        alt="Sosa & Arvelo LLC"
        width={355}
        height={200}
        className="h-12 w-auto"
      />
    ),
  },
];

const CustomerStories: React.FC = () => {
  return (
    <section
      aria-label="Customer stories"
      className="bg-[#f6f8fb] py-20 sm:py-24"
      id="customer-stories">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-primary-600/50" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
              Customer stories
            </span>
          </div>
          <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-[2.6rem] sm:leading-[1.15]">
            From binders and buried files{' '}
            <span className="font-display font-medium italic text-primary-600">
              to instant answers.
            </span>
          </h2>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-5">
          {/* Featured — MAXeta */}
          <figure className="relative flex flex-col overflow-hidden rounded-[28px] bg-white shadow-sm ring-1 ring-gray-100 transition-shadow duration-300 hover:shadow-md lg:col-span-3">
            <div className="relative aspect-[2.6/1] w-full">
              <Image
                src={featured.image}
                alt="Transmission grid on a clear morning"
                fill
                className="object-cover object-[50%_35%]"
              />
            </div>
            <div className="relative flex flex-1 flex-col p-8 sm:p-10 sm:pt-8">
              <span
                aria-hidden
                className="pointer-events-none absolute -right-3 -top-10 font-display text-[11rem] font-light italic leading-none text-primary-600/[0.06]">
                &rdquo;
              </span>
              <blockquote className="relative font-display text-2xl font-medium italic leading-snug text-gray-900 sm:text-[1.7rem]">
                &ldquo;{featured.quote}&rdquo;
              </blockquote>
              <p className="relative mt-5 max-w-lg text-sm leading-relaxed text-gray-500">
                {featured.detail}
              </p>
              <figcaption className="relative mt-8 flex flex-wrap items-end justify-between gap-4 border-t border-gray-100 pt-6">
                <div>
                  {featured.wordmark}
                  <div className="mt-1 text-xs text-gray-400">{featured.descriptor}</div>
                </div>
                <Link
                  href={featured.caseStudy}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary-600/25 px-4 py-2 text-sm font-semibold text-primary-600 transition-colors duration-200 hover:bg-primary-600 hover:text-white">
                  Read the case study
                  <Icon icon="tabler:arrow-right" className="text-base" />
                </Link>
              </figcaption>
            </div>
          </figure>

          {/* Stacked pair */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            {stories.map((story) => (
              <figure
                key={story.descriptor}
                className="flex flex-1 flex-col overflow-hidden rounded-[28px] bg-white shadow-sm ring-1 ring-gray-100 transition-shadow duration-300 hover:shadow-md">
                {story.image && (
                  <div className="relative aspect-[2.4/1] w-full">
                    <Image
                      src={story.image}
                      alt={`${story.company} photo`}
                      fill
                      className={`object-cover ${story.imagePos ?? 'object-center'}`}
                    />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-7">
                  <blockquote className="flex-1 font-display text-lg font-medium italic leading-snug text-gray-800">
                    &ldquo;{story.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6 flex flex-wrap items-end justify-between gap-3 border-t border-gray-100 pt-5">
                    <div>
                      {story.wordmark}
                      <div className="mt-1 text-xs text-gray-400">{story.descriptor}</div>
                    </div>
                    {story.caseStudy && (
                      <Link
                        href={story.caseStudy}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700">
                        Case study
                        <Icon icon="tabler:arrow-right" className="text-sm" />
                      </Link>
                    )}
                  </figcaption>
                </div>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerStories;
